import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../UserContext';
import axios from '../axiosInstance';
import {
  FaUsers, FaUserShield, FaExchangeAlt, FaExclamationTriangle, FaFileAlt, FaSyncAlt,
  FaBalanceScale, FaInfoCircle, FaSearch, FaBell, FaBoxOpen, FaClock, FaChartPie, FaRedoAlt
} from 'react-icons/fa';
import '../Pages.css';
import '../Pips/Pips.css';   // design system
import './Dashboard.css';

// ---------- formatting ----------
const fmtWhen = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '—';
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
const money = (n) => `N$ ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (n) => Number(n || 0).toLocaleString();
const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
};
const label = (s) => String(s || '').replace(/_/g, ' ');

// Clickable element that isn't a <button> (the app styles every <button> as a
// teal action button).
const clickable = (onClick) => ({
  role: 'button',
  tabIndex: 0,
  onClick,
  onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }
});

// ---------- small building blocks ----------
function Panel({ icon, title, action, onAction, children, wide }) {
  return (
    <section className={`dash-panel${wide ? ' dash-panel--wide' : ''}`}>
      <header className="dash-panel__head">
        <h3 className="dash-panel__title">{icon} {title}</h3>
        {action && <span className="dash-panel__link" {...clickable(onAction)}>{action} →</span>}
      </header>
      <div className="dash-panel__body">{children}</div>
    </section>
  );
}

// Horizontal bar per item, scaled to the largest value.
function Bars({ items }) {
  const max = Math.max(1, ...items.map((i) => i.value || 0));
  return (
    <div className="dash-bars">
      {items.map((i) => (
        <div className="dash-bar" key={i.label}>
          <span className="dash-bar__label">{i.label}</span>
          <span className="dash-bar__track">
            <span className={`dash-bar__fill dash-tone--${i.tone || 'teal'}`} style={{ width: `${((i.value || 0) / max) * 100}%` }} />
          </span>
          <span className="dash-bar__value">{num(i.value)}</span>
        </div>
      ))}
    </div>
  );
}

// Used / limit progress bar ("Unlimited" when there's no limit).
function Usage({ title, used, limit }) {
  const unlimited = limit === null || limit === undefined;
  const pct = unlimited ? 0 : (limit > 0 ? Math.min(100, (used / limit) * 100) : 100);
  const tone = unlimited ? 'teal' : pct >= 100 ? 'red' : pct >= 80 ? 'orange' : 'green';
  const left = unlimited ? null : Math.max(0, limit - used);
  return (
    <div className="dash-usage">
      <div className="dash-usage__top">
        <span className="dash-usage__title">{title}</span>
        <span className="dash-usage__nums">
          <strong>{num(used)}</strong> {unlimited ? 'used · Unlimited' : `of ${num(limit)} used`}
        </span>
      </div>
      <span className="dash-bar__track dash-usage__track">
        <span className={`dash-bar__fill dash-tone--${tone}`} style={{ width: `${unlimited ? 100 : pct}%`, opacity: unlimited ? 0.35 : 1 }} />
      </span>
      {!unlimited && (
        <span className={`dash-usage__left dash-text--${tone}`}>
          {left === 0 ? 'None left' : `${num(left)} left`}
        </span>
      )}
    </div>
  );
}

// One stacked bar split into coloured segments, with a legend.
function Stacked({ segments }) {
  const total = segments.reduce((s, x) => s + (x.value || 0), 0);
  return (
    <>
      <div className="dash-stack">
        {total === 0
          ? <span className="dash-stack__empty">No data yet</span>
          : segments.filter((s) => s.value > 0).map((s) => (
              <span key={s.label} className={`dash-stack__seg dash-tone--${s.tone}`}
                    style={{ width: `${(s.value / total) * 100}%` }} title={`${s.label}: ${s.value}`} />
            ))}
      </div>
      <div className="dash-legend">
        {segments.map((s) => (
          <span key={s.label} className="dash-legend__item">
            <span className={`dash-legend__dot dash-tone--${s.tone}`} /> {s.label} <strong>{num(s.value)}</strong>
          </span>
        ))}
      </div>
    </>
  );
}

function Stat({ value, caption, tone }) {
  return (
    <div className="dash-mini">
      <span className={`dash-mini__value${tone ? ` dash-text--${tone}` : ''}`}>{value}</span>
      <span className="dash-mini__caption">{caption}</span>
    </div>
  );
}

function Dashboard() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadedAt, setLoadedAt] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.get('/dashboard');
      setData(r.data);
      setLoadedAt(new Date());
    } catch (e) {
      setError(e.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (!user) return null;

  const org = data?.organisation || null;
  const pkg = org?.package || null;
  const s = data?.searches || null;
  const c = data?.customers || null;
  const cs = data?.cases || null;
  const tm = data?.tm_alerts || null;
  const tx = data?.transactions || null;
  const str = data?.str_reports || null;
  const rv = data?.customer_reviews || null;
  const hits = data?.screening || null;
  const activity = Array.isArray(data?.recent_activity) ? data.recent_activity : [];
  const hasAny = data && Object.keys(data).length > 0;

  const searches30 = s ? ['pip', 'un', 'uk'].reduce((t, k) => t + (s[k]?.last_30d || 0), 0) : 0;
  const name = user.first_name || (user.email || '').split('@')[0];
  const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  // Headline tiles — only the ones the user can see
  const tiles = [
    c && { key: 'customers', icon: <FaUsers />, tone: 'teal', value: num(c.total), label: 'Active customers',
           sub: c.edd_pending ? `${c.edd_pending} need EDD` : 'No EDD outstanding', to: '/customers' },
    hits && { key: 'hits', icon: <FaBalanceScale />, tone: hits.pending_review ? 'orange' : 'green', value: num(hits.pending_review),
              label: 'Hits to review', sub: `${num(hits.last_30d)} raised in 30 days`, to: '/customers' },
    cs && { key: 'cases', icon: <FaUserShield />, tone: 'blue', value: num(cs.open), label: 'Open cases',
            sub: `${num(cs.my_open)} assigned to me`, to: '/cases' },
    cs && { key: 'overdue', icon: <FaClock />, tone: cs.overdue ? 'red' : 'green', value: num(cs.overdue), label: 'Overdue cases',
            sub: cs.overdue ? 'Past their SLA' : 'All within SLA', to: '/cases' },
    tm && { key: 'tm', icon: <FaBell />, tone: tm.pending ? 'purple' : 'green', value: num(tm.pending), label: 'Pending TM alerts',
            sub: `${num(tm.total)} in total`, to: '/tm-alerts' },
    s && { key: 'searches', icon: <FaSearch />, tone: 'blue', value: num(searches30), label: 'Searches (30 days)',
           sub: 'PIP, UN and UK screening', to: '/Pips/pips' }
  ].filter(Boolean);

  return (
    <div className="page-container dash">
      {/* Welcome band */}
      <div className="dash-hero">
        <div>
          {/* not an <h2>: .page-container h2 (Pages.css) restyles those as page titles */}
          <div className="dash-hero__title">{greeting()}, {name}</div>
          <p className="dash-hero__sub">
            {org?.name ? <><strong>{org.name}</strong>{pkg?.package ? ` · ${pkg.package} package` : ''} · </> : null}
            {today}
          </p>
        </div>
        <div className="dash-hero__actions">
          {loadedAt && <span className="dash-hero__updated">Updated {fmtWhen(loadedAt)}</span>}
          <span className={`dash-hero__refresh${loading ? ' is-busy' : ''}`} {...clickable(() => { if (!loading) fetchAll(); })}>
            <FaRedoAlt className={loading ? 'dash-spin' : ''} /> {loading ? 'Refreshing…' : 'Refresh'}
          </span>
        </div>
      </div>

      {error && (
        <div className="alert-banner alert-banner--danger" style={{ marginBottom: '1rem' }}>
          <span className="alert-banner__body"><FaInfoCircle /> {error}</span>
        </div>
      )}

      {!data && loading && <p className="dash-muted">Loading dashboard…</p>}

      {data && !hasAny && (
        <div className="alert-banner alert-banner--warning">
          <span className="alert-banner__body"><FaInfoCircle /> No dashboard data available for your permissions yet.</span>
        </div>
      )}

      {/* Headline tiles */}
      {tiles.length > 0 && (
        <div className="dash-tiles">
          {tiles.map((t) => (
            <div key={t.key} className={`dash-tile dash-tile--${t.tone}`} {...clickable(() => navigate(t.to))}>
              <span className={`dash-tile__icon dash-tone--${t.tone}`}>{t.icon}</span>
              <span className="dash-tile__body">
                <span className="dash-tile__value">{t.value}</span>
                <span className="dash-tile__label">{t.label}</span>
                <span className="dash-tile__sub">{t.sub}</span>
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Package-limit warnings */}
      {pkg && pkg.screeningLimit != null && pkg.screeningsLeft === 0 && (
        <div className="stat-limit-warning" style={{ marginBottom: '1rem' }}>
          <strong>Warning:</strong> This organisation has reached the maximum number of single searches allowed by their package.
          Users will not be able to perform additional single searches until the package is upgraded.
        </div>
      )}
      {pkg && pkg.batchScreeningLimit != null && pkg.batchScreeningsLeft === 0 && (
        <div className="stat-limit-warning" style={{ marginBottom: '1rem' }}>
          <strong>Warning:</strong> This organisation has reached the maximum number of bulk searches allowed by their package.
          Users will not be able to perform additional bulk searches until the package is upgraded.
        </div>
      )}

      <div className="dash-grid">
        {pkg && (
          <Panel icon={<FaBoxOpen />} title={`Package usage — ${pkg.package}`}>
            <Usage title="Single screenings" used={pkg.screeningsDone || 0} limit={pkg.screeningLimit} />
            <Usage title="Bulk screenings" used={pkg.batchScreeningsDone || 0} limit={pkg.batchScreeningLimit} />
          </Panel>
        )}

        {s && (s.pip || s.un || s.uk) && (
          <Panel icon={<FaSearch />} title="Screening activity — last 30 days" action="Search PIPs" onAction={() => navigate('/Pips/pips')}>
            <Bars items={[
              s.pip && { label: 'PIP', value: s.pip.last_30d, tone: 'teal' },
              s.un && { label: 'UN Sanctions', value: s.un.last_30d, tone: 'blue' },
              s.uk && { label: 'UK Sanctions', value: s.uk.last_30d, tone: 'purple' }
            ].filter(Boolean)} />
            <div className="dash-minis">
              {s.pip && <Stat value={num(s.pip.total)} caption="PIP searches in total" />}
              {s.un && <Stat value={num(s.un.total)} caption="UN searches in total" />}
              {s.uk && <Stat value={num(s.uk.total)} caption="UK searches in total" />}
              <Stat value={num(['pip', 'un', 'uk'].reduce((t, k) => t + (s[k]?.mine || 0), 0))} caption="By me" />
            </div>
          </Panel>
        )}

        {c && (
          <Panel icon={<FaChartPie />} title="Customer risk" action="Open KYC" onAction={() => navigate('/customers')}>
            <Stacked segments={[
              { label: 'Low', value: c.by_risk_tier?.low || 0, tone: 'green' },
              { label: 'Medium', value: c.by_risk_tier?.medium || 0, tone: 'orange' },
              { label: 'High', value: c.by_risk_tier?.high || 0, tone: 'red' },
              ...(c.by_risk_tier?.unknown ? [{ label: 'Unscored', value: c.by_risk_tier.unknown, tone: 'grey' }] : [])
            ]} />
            <div className="dash-minis">
              <Stat value={num(c.by_status?.pending)} caption="Pending approval" tone={c.by_status?.pending ? 'orange' : undefined} />
              <Stat value={num(c.by_status?.active)} caption="Active" tone="green" />
              <Stat value={num(c.edd_pending)} caption="EDD outstanding" tone={c.edd_pending ? 'red' : undefined} />
              {hits && <Stat value={num(hits.total_hits)} caption="Screening hits in total" />}
            </div>
          </Panel>
        )}

        {cs && (
          <Panel icon={<FaUserShield />} title="Cases" action="Open cases" onAction={() => navigate('/cases')}>
            <Bars items={[
              { label: 'Critical', value: cs.open_by_priority?.critical || 0, tone: 'red' },
              { label: 'High', value: cs.open_by_priority?.high || 0, tone: 'orange' },
              { label: 'Medium', value: cs.open_by_priority?.medium || 0, tone: 'blue' },
              { label: 'Low', value: cs.open_by_priority?.low || 0, tone: 'green' }
            ]} />
            <div className="dash-minis">
              <Stat value={num(cs.open)} caption="Open" />
              <Stat value={num(cs.my_open)} caption="Assigned to me" />
              <Stat value={num(cs.overdue)} caption="Past SLA" tone={cs.overdue ? 'red' : 'green'} />
              <Stat value={num(cs.total)} caption="All time" />
            </div>
          </Panel>
        )}

        {(tm || tx) && (
          <Panel icon={<FaExchangeAlt />} title="Transaction monitoring" action="Open alerts" onAction={() => navigate('/tm-alerts')}>
            {tm && (
              <Bars items={[
                { label: 'Critical', value: tm.pending_by_severity?.critical || 0, tone: 'red' },
                { label: 'High', value: tm.pending_by_severity?.high || 0, tone: 'orange' },
                { label: 'Medium', value: tm.pending_by_severity?.medium || 0, tone: 'blue' },
                { label: 'Low', value: tm.pending_by_severity?.low || 0, tone: 'green' }
              ]} />
            )}
            <div className="dash-minis">
              {tm && <Stat value={num(tm.pending)} caption="Pending alerts" tone={tm.pending ? 'purple' : undefined} />}
              {tx && <Stat value={num(tx.last_30d_count)} caption="Transactions (30 days)" />}
              {tx && <Stat value={money(tx.last_30d_total)} caption="Value (30 days)" />}
            </div>
          </Panel>
        )}

        {(rv || str) && (
          <Panel icon={<FaFileAlt />} title="Reviews & reporting">
            {rv && (
              <>
                <div className="dash-subhead"><FaSyncAlt /> Periodic CDD reviews</div>
                <div className="dash-minis">
                  <Stat value={num(rv.open)} caption="Open" />
                  <Stat value={num(rv.overdue)} caption="Overdue" tone={rv.overdue ? 'red' : undefined} />
                  <Stat value={num(rv.due_soon)} caption="Due in 30 days" tone={rv.due_soon ? 'orange' : undefined} />
                  <Stat value={num(rv.completed_last_30d)} caption="Completed (30 days)" tone="green" />
                </div>
              </>
            )}
            {str && (
              <>
                <div className="dash-subhead"><FaFileAlt /> Suspicious transaction reports — {num(str.total)}</div>
                {Object.keys(str.by_status || {}).length === 0
                  ? <p className="dash-muted">No reports yet.</p>
                  : <div className="dash-legend">
                      {Object.entries(str.by_status).map(([k, v]) => (
                        <span key={k} className="dash-legend__item dash-chip">{label(k)} <strong>{num(v)}</strong></span>
                      ))}
                    </div>}
              </>
            )}
          </Panel>
        )}

        {cs && (
          <Panel icon={<FaClock />} title="Recent case activity" wide>
            {activity.length === 0 ? (
              <p className="dash-muted">No case activity yet.</p>
            ) : (
              <ul className="dash-timeline">
                {activity.map((ev, i) => (
                  <li key={i} className="dash-timeline__item" onClick={() => navigate(`/cases?id=${ev.case_id}`)}>
                    <span className="dash-timeline__dot" />
                    <div className="dash-timeline__main">
                      <div className="dash-timeline__top">
                        <span className="dash-chip">{label(ev.event_type)}</span>
                        <strong>{ev.case_number}</strong>
                        <span className="dash-muted">{ev.actor_email || 'system'}</span>
                        <span className="dash-timeline__when">{fmtWhen(ev.created_at)}</span>
                      </div>
                      {ev.description && <div className="dash-timeline__desc">{ev.description}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        )}
      </div>

      {data && hasAny && tiles.length === 0 && (
        <p className="dash-muted" style={{ marginTop: '1rem' }}>
          <FaExclamationTriangle /> Panels appear here for the modules your organisation has and your role can read.
        </p>
      )}
    </div>
  );
}

export default Dashboard;
